import {
	DragDropContext,
	Draggable,
	DragStart,
	Droppable,
	DropResult,
	OnDragEndResponder,
	OnDragStartResponder,
} from "@hello-pangea/dnd";
import { Box, Tabs } from "@mui/material";
import { Stack } from "@samdesk/components/Stack";
import layout from "common/layoutDefinitions";
import samdeskLogo from "images/samdeskLogo.png";
import { debounce } from "lodash";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import theme from "theme";
import { getTabPath } from "utils/tabUtils";
import useLogToMixPanel from "utils/useLogToMixpanel";
import Menu from "./Menu";
import Search from "./Search";
import { SeeAllTab } from "./SeeAllTab";
import Tab from "./Tab";
import useManageDeepLinkedTabs from "./useManageDeepLinkedTabs";
import { useOrderedDashboardTabs } from "./useOrderedDashboardTabs";

const NavBar = () => {
	const [numVisibleTabs, setNumVisibleTabs] = useState(0);

	const updateNumVisibleTabs = useMemo(
		() =>
			debounce(() => {
				const searchAndMenuWidth = 221;
				const navBarPadding =
					56 + // Outer padding
					16; // Space between 'See all' button and Search button
				const seeAllTabsButtonWidth = 54;

				const maxTabWidth = 208;

				const availableSpace =
					innerWidth -
					searchAndMenuWidth -
					navBarPadding -
					seeAllTabsButtonWidth;
				const numVisibleTabs = Math.floor(availableSpace / maxTabWidth);

				setNumVisibleTabs(Math.max(numVisibleTabs, 1)); // Always show at least one tab
			}, 200),
		[setNumVisibleTabs]
	);

	useEffect(() => {
		updateNumVisibleTabs();
	}, [updateNumVisibleTabs]);

	useEffect(() => {
		window.addEventListener("resize", updateNumVisibleTabs);

		return () => window.removeEventListener("resize", updateNumVisibleTabs);
	}, [updateNumVisibleTabs]);

	return (
		<Box sx={navBarContainerSx}>
			<TabBar numVisibleTabs={numVisibleTabs} />
			<Stack direction="row">
				<Search />
				<Box sx={actionsWrapper}>
					<Stack direction="row" alignItems="center">
						<img src={samdeskLogo} style={samdeskLogoStyle} />
						<Menu />
					</Stack>
				</Box>
			</Stack>
		</Box>
	);
};

export default NavBar;

const TabBar = ({ numVisibleTabs }: { numVisibleTabs: number }) => {
	const tabsRef = useRef<(HTMLDivElement | null)[]>([]);

	const { pathname } = useLocation();
	const { dashboardTabs, updateTabOrder } = useOrderedDashboardTabs();

	useManageDeepLinkedTabs();

	const { logToMixpanel } = useLogToMixPanel();

	const allTabs = useMemo(() => dashboardTabs ?? [], [dashboardTabs]);
	const showSeeAllTabsButton = allTabs.length > numVisibleTabs;

	const currentTabIndex = useMemo(
		() => allTabs.findIndex((tab) => pathname.includes(`/${getTabPath(tab)}`)),
		[allTabs, pathname]
	);

	const currentTab = useMemo(
		() => allTabs[currentTabIndex],
		[allTabs, currentTabIndex]
	);

	const isValidTabIndex = currentTabIndex !== -1;

	useEffect(() => {
		tabsRef.current[currentTabIndex]?.scrollIntoView({
			behavior: "smooth",
		});
	}, [currentTabIndex]);

	const logReorderCompleted = (
		keys: string[],
		sourceIndex: number,
		destinationIndex: number
	) => {
		const keysAfter: string[] = Array.from(keys);
		const key = keysAfter.splice(sourceIndex, 1)[0];
		keysAfter.splice(destinationIndex, 0, key);
		logToMixpanel("tab_reorder_completed", {
			original_index: sourceIndex,
			new_index: destinationIndex,
			tab_id: key,
			tab_ids_before: keys,
			tab_ids_after: keysAfter,
			tab_type: allTabs[sourceIndex].__typename,
		});
	};

	const handleDragStart: OnDragStartResponder = (start: DragStart) => {
		logToMixpanel("tab_reorder_started", {
			original_index: start.source.index,
			total_tabs: allTabs.length,
			tab_id: allTabs[start.source.index].key,
			tab_type: allTabs[start.source.index].__typename,
		});
	};

	const handleDragEnd: OnDragEndResponder = (result: DropResult) => {
		if (
			result.destination &&
			result.source.index !== result.destination.index
		) {
			logReorderCompleted(
				allTabs.map((tab) => tab.key),
				result.source.index,
				result.destination.index
			);

			updateTabOrder(result.source.index, result.destination.index);
		} else {
			const keys = allTabs.map((tab) => tab.key);
			logToMixpanel("tab_reorder_cancelled", {
				original_index: result.source.index,
				new_index: result.source.index,
				tab_id: allTabs[result.source.index].key,
				tab_ids_before: keys,
				tab_ids_after: keys,
				tab_type: allTabs[result.source.index].__typename,
			});
		}
	};

	const handleOverflowButtonClicked = () =>
		logToMixpanel("overflow_opened", {
			selected_tab_index: currentTabIndex,
			selected_tab_id: isValidTabIndex ? allTabs[currentTabIndex].key : "",
			total_tabs: allTabs.length,
			tab_ids: allTabs.map((tab) => tab.key),
		});

	return (
		<>
			<div style={scrollingContainer}>
				<Stack direction="row">
					<DragDropContext
						onDragStart={handleDragStart}
						onDragEnd={handleDragEnd}
					>
						<Droppable direction="horizontal" droppableId="mainTabs">
							{(droppableProvided) => (
								<Tabs
									ref={droppableProvided.innerRef}
									{...droppableProvided.droppableProps}
									TabIndicatorProps={tabIndicatorProps}
									orientation="horizontal"
									sx={tabsContainerSx}
									value={isValidTabIndex ? currentTabIndex : false}
								>
									{allTabs.map((tab, index) => (
										<Draggable
											draggableId={tab.key}
											key={tab.key}
											index={index}
											disableInteractiveElementBlocking={true}
										>
											{(draggableProvided, draggableSnapshot) => (
												<div
													ref={(element) => {
														draggableProvided.innerRef(element);
														tabsRef.current[index] = element;
													}}
													{...draggableProvided.draggableProps}
													{...draggableProvided.dragHandleProps}
												>
													<Tab
														isCurrentTab={currentTab?.key === tab.key}
														isOverflowComponent={false}
														tab={tab}
														isDragging={draggableSnapshot.isDragging}
													/>
												</div>
											)}
										</Draggable>
									))}
									{droppableProvided.placeholder}
								</Tabs>
							)}
						</Droppable>
					</DragDropContext>
				</Stack>
			</div>
			{showSeeAllTabsButton && (
				<div style={{ height: "50px" }} onClick={handleOverflowButtonClicked}>
					<SeeAllTab tabs={allTabs} currentTabIndex={currentTabIndex} />
				</div>
			)}
		</>
	);
};

/** STYLES */

const samdeskLogoStyle = {
	width: "calc(var(--space-large) + var(--space-smallest))",
	height: "calc(var(--space-large)",
};

const actionsWrapper = {
	border: "1px solid var(--color-border)",
	paddingLeft: "var(--space-base)",
	marginBottom: "var(--space-small)",
	borderRadius: "var(--radius-base)",
};

const navBarContainerSx = {
	alignItems: "flex-end",
	backgroundColor: "background.default",
	borderBottom: `${layout.navBar.border}px solid ${theme.palette.border.main}`,
	display: "flex",
	height: `${layout.navBar.height}px`,
	justifyContent: "space-between",
	padding:
		"0 var(--space-base) 0 calc(var(--space-larger) + var(--space-small))",
	position: "relative",
	gap: "var(--space-base)",
};

const scrollingContainer: CSSProperties = {
	overflow: "auto",
	scrollbarWidth: "none",
};

const tabsContainerSx = {
	"& .MuiTabs-flexContainer": {
		alignItems: "flex-end",
		height: "60px",
	},
	overflow: "unset",
};

const tabIndicatorProps = {
	style: { display: "none" },
};
